import React, { Component } from "react";
import { hasBecomeTrue } from "~/hocs/crud";
import { messages } from "./../../i18n-messages";
import { InjectedIntlProps, injectIntl } from "@ai360/core/intl";
import { model, service } from "./../data";
import { getAgBytesErrorClassNames, onTextChange, onNumberChange } from "~/admin/utils";
// constants
import * as picklistService from "~/core/picklist/picklist-names";
// Components
import { TextInput, NumericInput, Section, SubSection, Checkbox } from "~/core";
import TraitCharacteristics from "./../components/trait-characteristics";
import TraitSpecifics from "./../components/trait-specifics";
import DeveloperList from "./../components/developer-list";
import { IAddEditProps } from "../interfaces";
import { adminData } from "~/admin/data";

export class AddEditPanel extends Component<IAddEditProps & InjectedIntlProps, any> {
    static GUID = "guid";
    static PICKLIST_GUID = "picklistValueGuid";
    gene: typeof adminData;

    constructor(props) {
        super(props);
        this.gene = props.record || {};
        this.state = {
            nextId: null,
        };
    }

    componentDidMount() {
        this.setupGeneRecord();
        this.props.setBreadcrumbs([""]);
        const { needs } = this.props;
        needs([this.props.getNextId(), this.props.fetchPicklistData()]);

        if (this.props.recordGuid) {
            needs([this.props.fetchRecord(this.props.recordGuid)]);
        }
    }

    componentDidUpdate(prevProps) {
        if (hasBecomeTrue("fetchData", prevProps, this.props)) {
            this.props.liftRecordData(this.gene);
        }

        if (this.props.addEditPanel.mode === "ADD") {
            this.setupNextId(this.props);
        } else {
            if (this.props.record && this.props.record !== prevProps.record) {
                this.gene = this.props.record;
            }
        }
    }

    setupNextId = (props) => {
        if (props.nextId && props.nextId !== this.state.nextId) {
            this.gene[model.PROPS_GENE_ID] = "" + props.nextId;
            this.setState({
                nextId: props.nextId,
            });
        }
    };

    setupGeneRecord = () => {
        const { addEditPanel } = this.props;
        this.gene = addEditPanel.record || {};
        if (addEditPanel.mode === "ADD") {
            this.gene = service.getDefaultRecord();
        }
    };

    onTextChange = (formKey, value, callback?: () => void) => {
        if (
            formKey === model.PROPS_DEVELOPER_NAMES ||
            formKey === model.PROPS_TRAIT_CHARACTERISTIC_NAMES ||
            formKey === model.PROPS_TRAIT_SPECIFIC_NAMES
        ) {
            const valueArr = [];
            value.forEach((val) => {
                for (const prop in val) {
                    valueArr.push(val[prop]);
                }
            });
            this.gene = onTextChange(this.gene, { formKey: [formKey], value: valueArr }, callback);
        } else {
            this.gene = onTextChange(this.gene, { formKey: [formKey], value }, callback);
        }
    };

    onNumberChange = (formKey, value, callback?: () => void) => {
        this.gene = onNumberChange(this.gene, { formKey: [formKey], value }, callback);
    };

    setHeaderText = () => {
        this.props.setBreadcrumbs([this.gene[model.PROPS_GENE_NAME]]);
    };

    renderGeneInfo = () => {
        const { apiErrors, userRole } = this.props;
        const { formatMessage } = this.props.intl;
        const { gene } = this;
        return (
            <div className="section-container">
                <Section testId="gene-general-section">
                    <SubSection testId="gene-gene-id-subsection">
                        <NumericInput
                            tabIndex={0}
                            scale={0}
                            precision={9}
                            suppressFormatting
                            containerClassNames={getAgBytesErrorClassNames([180, 181], apiErrors, [
                                "form-input-id",
                            ])}
                            placeholderText={formatMessage(messages.geneId)}
                            value={gene[model.PROPS_GENE_ID]}
                            onChange={(value) => this.onNumberChange(model.PROPS_GENE_ID, value)}
                            required
                            testId="gene-gene-id-input"
                        />
                        {!userRole[model.PROPS_ACTIVE_INACTIVE] ? null : (
                            <Checkbox
                                className="active-checkbox"
                                onChange={(e, value) =>
                                    this.onTextChange(model.PROPS_ACTIVE_YN, value)
                                }
                                value={gene[model.PROPS_ACTIVE_YN]}
                                label={formatMessage(messages.active)}
                                testId="gene-is-active-checkbox"
                            />
                        )}
                    </SubSection>
                    <SubSection testId="gene-gene-name-subsection">
                        <TextInput
                            tabIndex={0}
                            maxLength={50}
                            containerClassNames={getAgBytesErrorClassNames([178, 179], apiErrors)}
                            placeholderText={formatMessage(messages.geneName)}
                            value={gene[model.PROPS_GENE_NAME]}
                            autoFocus
                            onChange={(value) =>
                                this.onTextChange(model.PROPS_GENE_NAME, value, this.setHeaderText)
                            }
                            required
                            testId="gene-gene-name-input"
                        />
                    </SubSection>
                </Section>
            </div>
        );
    };

    render() {
        const { formatMessage } = this.props.intl;
        return (
            <div className="add-edit-panel">
                {this.renderGeneInfo()}
                <div className="section-container">
                    <Section
                        required
                        className="grid-section"
                        headerText={formatMessage(messages.traitCharacteristic, { count: 0 })}
                        testId="gene-trait-characteristics-section"
                    >
                        <TraitCharacteristics
                            apiErrors={this.props.apiErrors}
                            formatMessage={formatMessage}
                            options={this.props[picklistService.PICKLIST_TRAIT_CHARACTERISTICS]}
                            record={(this.gene[model.PROPS_TRAIT_CHARACTERISTIC_NAMES] || []).map(
                                (traitCharacteristicName) => ({
                                    traitCharacteristicName,
                                })
                            )}
                            itemListAlias={model.PROPS_TRAIT_CHARACTERISTIC_NAME}
                            onTextChange={(e, value) =>
                                this.onTextChange(model.PROPS_TRAIT_CHARACTERISTIC_NAMES, value)
                            }
                        />
                    </Section>
                    <span className="bar section-fence"></span>
                    <Section
                        className="grid-section"
                        headerText={formatMessage(messages.traitSpecific, {
                            count: 0,
                        })}
                        testId="gene-trait-specifics-section"
                    >
                        <TraitSpecifics
                            formatMessage={formatMessage}
                            options={this.props[picklistService.PICKLIST_TRAIT_SPECIFICS]}
                            record={(this.gene[model.PROPS_TRAIT_SPECIFIC_NAMES] || []).map(
                                (traitSpecificName) => ({
                                    traitSpecificName,
                                })
                            )}
                            itemListAlias={model.PROPS_TRAIT_SPECIFIC_NAME}
                            onTextChange={(e, value) =>
                                this.onTextChange(model.PROPS_TRAIT_SPECIFIC_NAMES, value)
                            }
                        />
                    </Section>
                    <span className="bar section-fence"></span>
                    <Section
                        required
                        className="grid-section"
                        headerText={formatMessage(messages.developer, {
                            count: 0,
                        })}
                        testId="gene-developers-section"
                    >
                        <DeveloperList
                            apiErrors={this.props.apiErrors}
                            formatMessage={formatMessage}
                            options={this.props[picklistService.PICKLIST_DEVELOPER]}
                            record={(this.gene[model.PROPS_DEVELOPER_NAMES] || []).map(
                                (developerName) => ({ developerName })
                            )}
                            itemListAlias={model.PROPS_DEVELOPER_NAME}
                            onTextChange={(e, value) =>
                                this.onTextChange(model.PROPS_DEVELOPER_NAMES, value)
                            }
                        />
                    </Section>
                </div>
            </div>
        );
    }
}

export default injectIntl(AddEditPanel);
